/**
 * @fileoverview 入力値 1 件に対するバリデーション設定（ValidationScheme）の評価
 * @description ValidationProvider から切り出した純粋な判定ロジック。UI（store・トースト）には触らない。
 */
import { VALIDATION_MESSAGE_TYPES } from '@/constants/validationMessages'
import { PHONE_NUMBER_BLACKLIST } from '@/constants/phoneNumberBlacklist'
import {
  isValidEmail,
  isValidMailDomain,
  isValidURL,
  isValidPassword,
  isValidNumber,
  isValidKana,
  isValidDate,
  isValidPhoneNumberFormat,
  normalizePhoneNumber,
  verifyPhoneNumber,
  isInputed,
} from '@/utilities/validator'

export interface ValidateValueOptions {
  /**
   * チェックボックス等で「未選択」を表す明示的な値
   * @description 指定がある場合、required の判定は空欄チェックではなくこの値との比較で行う
   */
  falseValue?: string | number | boolean,

  /**
   * mailDomain 判定で弾くドメインの一覧
   */
  invalidMailDomains?: string[],
}

export type ValidateValueResult =
  | { valid: true }
  | {
    valid: false,

    /**
     * 項目の直下に表示するエラーメッセージ
     */
    message: string,

    /**
     * メッセージとは別に画面全体へ通知したい補足（トースト等）
     */
    notice?: string,
  }

/**
 * バリデーション設定に有効な項目が 1 つでもあるかどうか
 */
export function hasValidationScheme(scheme?: ValidationScheme): boolean {
  if (!scheme) return false
  return Object.values(scheme).some(v => !!v)
}

const fail = (message: string, notice?: string): ValidateValueResult => ({ valid: false, message, notice })

/**
 * 文字数の範囲チェック
 * @description maxLength / minLength の両方が指定されていれば範囲として、片方だけなら上限・下限として判定する。境界値はどちらも許容する。
 */
function validateLength(text: string, scheme: ValidationScheme, messages: ValidationMessages): ValidateValueResult | undefined {
  const max = scheme.maxLength && scheme.maxLength > 0 ? scheme.maxLength : undefined
  const min = scheme.minLength && scheme.minLength > 0 ? scheme.minLength : undefined

  if (max !== undefined && min !== undefined) {
    // 「最大」「最小」が逆に指定されていても範囲として扱う
    const [lower, upper] = max >= min ? [min, max] : [max, min]

    if (text.length < lower || text.length > upper) {
      return fail((messages.range || VALIDATION_MESSAGE_TYPES.OUT_OF_RANGE)
        .replace(/{{ min }}/, `${lower}文字`)
        .replace(/{{ max }}/, `${upper}文字`))
    }
    return undefined
  }

  if (max !== undefined && text.length > max) {
    return fail(messages.maxLength || VALIDATION_MESSAGE_TYPES.OVER_MAX_LENGTH)
  }

  if (min !== undefined && text.length < min) {
    return fail(messages.minLength || VALIDATION_MESSAGE_TYPES.BELOW_MIN_LENGTH)
  }

  return undefined
}

/**
 * 入力値をバリデーション設定に沿って検査する
 * @description 設定のうち有効になっているもののみを順に評価し、最初に引っかかった時点で判定を終える。
 *              required 以外の判定は値が入力されている場合のみ行う。
 *
 * @param value 検査対象の値
 * @param scheme バリデーション設定
 * @param messages 種別ごとのエラーメッセージ。未指定の種別はデフォルト文言になる
 * @param options 判定の補助情報
 */
export async function validateValue(
  value: InputValue | undefined,
  scheme: ValidationScheme,
  messages: ValidationMessages,
  options: ValidateValueOptions = {},
): Promise<ValidateValueResult> {
  if (scheme.required) {
    const isValid = options.falseValue !== undefined
      ? value !== options.falseValue
      : isInputed(value)

    if (!isValid) return fail(messages.required || VALIDATION_MESSAGE_TYPES.INPUT_REQUIRED)
  }

  if (!isInputed(value)) return { valid: true }

  const text = String(value)

  if (scheme.email && !isValidEmail(text)) {
    return fail(messages.email || VALIDATION_MESSAGE_TYPES.INVALID_EMAIL)
  }

  if (scheme.mailDomain && !isValidMailDomain(options.invalidMailDomains ?? [], text)) {
    return fail(messages.mailDomain || VALIDATION_MESSAGE_TYPES.INVALID_MAIL_DOMAIN)
  }

  if (scheme.url && !isValidURL(text)) {
    return fail(messages.url || VALIDATION_MESSAGE_TYPES.INVALID_URL)
  }

  if (scheme.password && !isValidPassword(text)) {
    return fail(messages.password || VALIDATION_MESSAGE_TYPES.INVALID_PASSWORD)
  }

  if (scheme.number && !isValidNumber(text)) {
    return fail(messages.number || VALIDATION_MESSAGE_TYPES.INVALID_NUMBER)
  }

  if (scheme.kana && !isValidKana(text)) {
    return fail(messages.kana || VALIDATION_MESSAGE_TYPES.INVALID_KANA)
  }

  if (scheme.date && !isValidDate(text)) {
    return fail(messages.date || VALIDATION_MESSAGE_TYPES.INVALID_DATE)
  }

  if (scheme.phoneNumber) {
    const message = messages.phoneNumber || VALIDATION_MESSAGE_TYPES.INVALID_PHONE_NUMBER

    if (PHONE_NUMBER_BLACKLIST.includes(normalizePhoneNumber(text))) return fail(message, '許可されない電話番号です')
    if (!isValidPhoneNumberFormat(text)) return fail(message, '半角数字のみで番号を入力してください')

    // 電話番号の実在性チェック（Twilio API）。照会できなかった場合は形式チェックのみで通す
    if (await verifyPhoneNumber(text) === 'invalid') return fail(message, '有効な電話番号ではありません')
  }

  return validateLength(text, scheme, messages) ?? { valid: true }
}
